import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

// File information interfaces
interface FileInfo {
  fileId: number;
  fileName: string;
  operatorByFileUploader: string;
  operatorByInboundPpo: string;
  operatorByOutboundPpo: string;
  accountingYear: number;
  accountingQuarter: number;
  fileVersion: number;
  creationDate: string;
  inboundRemovalDate: string | null;
  outboundRemovalDate: string | null;
  fileStatus: string;
  processDate: string;
  inboundAcceptanceDate?: string;
  outboundAcceptanceDate?: string;
  notifiedDate?: string;
  downloadDate?: string;
  validStatus?: string | null;
  samplesCount?: number;
  pendingOn?: string | null;
  loggedUserOperator?: string | null;
  deletedAt?: string | null;
  comment?: boolean;
  samples?: any;
}

interface FileCriteria {
  toYear: string;
  table: string;
  toQuarter: string;
  designatedOperator: string;
  fromQuarter: string;
  fromYear: string;
}

interface FileInfoStatus {
  filename: string;
  status: string;
}

interface RemunerationData {
  remunerationGroup: {
    id: number;
    name: string;
    operator: any;
    displayName: string;
    predefined: boolean;
    homeCountry: boolean;
  };
  mailFormat: string;
  mailCategory: string;
  accountingType: string;
  grossSampledFlow: number;
  gnAdjF: number | null;
  netPercentage: number | null;
  netSampledFlow: number;
  grossNonSampledFlow: number | null;
  adjFNonSampledFlow: number | null;
  netNonSampledFlow: number | null;
  nonExptIBRS: number | null;
  ibWeight: number | null;
  totalKg: number;
  rateKg: number;
  tdKg: number;
  ipk: number | null;
  itemsSampledFlow: number;
  itemsNonSampledFlow: number | null;
  totalItems: number;
  rateItem: number;
  tdItem: number;
  hasEno1Data: boolean;
  notSubjToSampling: boolean;
  // Formatted fields
  formatFormatted: string;
  mailCategoryFormatted: string;
  accountingTypeFormatted: string;
  grossSampledFlowFormatted: string;
  gnAdjFFormatted: string;
  netPercentageFormatted: string;
  netSampledFlowFormatted: string;
  grossNonSampledFlowFormatted: string;
  adjFNonSampledFlowFormatted: string;
  netNonSampledFlowFormatted: string;
  remunerationGroupFormatted: string;
  nonExptIbrsFormatted: string;
  ibWeightFormatted: string;
  totalKgFormatted: string;
  rateKgFormatted: string;
  tdKgFormatted: string;
  ipkFormatted: string;
  itemsSampledFlowFormatted: string;
  itemsNonSampledFlowFormatted: string;
  totalItemsFormatted: string;
  rateItemFormatted: string;
  tdItemFormatted: string;
}

interface PGEData {
  p: RemunerationData[];
  g: RemunerationData[];
  e: RemunerationData[];
  totalP: any;
  totalG: any;
  totalE: any;
}

interface CN61CrrHtmlData {
  eno1Files: FileInfo[];
  e55Files: FileInfo[];
  pgePriority: PGEData;
  pgeNonPriority: PGEData;
  fileCriteria: FileCriteria;
  eno1FileInfos?: FileInfoStatus[];
  e55FileInfos?: FileInfoStatus[];
}

interface CN61CrrHtmlTableProps {
  data: CN61CrrHtmlData;
  title: string;
}

function CN61CrrHtmlTable({ data, title }: CN61CrrHtmlTableProps): React.ReactElement {
  if (!data) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No CN61 CRR HTML data available
        </Typography>
      </Box>
    );
  }

  // Determine if this is inbound or outbound based on title
  const isInbound = title.toLowerCase().includes('inbound');

  const renderCombinedFileTable = () => {
    // Create maps for file status lookup by filename
    const eno1StatusMap = new Map<string, string>();
    const e55StatusMap = new Map<string, string>();
    
    // Populate status maps from FileInfos arrays
    data.eno1FileInfos?.forEach(info => {
      eno1StatusMap.set(info.filename, info.status);
    });
    
    data.e55FileInfos?.forEach(info => {
      e55StatusMap.set(info.filename, info.status);
    });
    
    // Create a map to combine ENO1 and E55 files by quarter
    const quarterMap = new Map<number, { eno1File?: FileInfo; e55File?: FileInfo }>();
    
    // Add ENO1 files to the map
    data.eno1Files?.forEach(file => {
      const quarter = file.accountingQuarter;
      if (!quarterMap.has(quarter)) {
        quarterMap.set(quarter, {});
      }
      quarterMap.get(quarter)!.eno1File = file;
    });
    
    // Add E55 files to the map
    data.e55Files?.forEach(file => {
      const quarter = file.accountingQuarter;
      if (!quarterMap.has(quarter)) {
        quarterMap.set(quarter, {});
      }
      quarterMap.get(quarter)!.e55File = file;
    });
    
    // Sort quarters
    const sortedQuarters = Array.from(quarterMap.keys()).sort();
    
    return (
      <Box sx={{ mb: 4 }}>
        <TableContainer component={Paper} sx={{ boxShadow: 1, mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Acc. Q.</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>eNO1 Filename</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>eNO1 Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>e55 Filename</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>e55 Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedQuarters.map((quarter) => {
                const quarterData = quarterMap.get(quarter)!;
                const eno1FileName = quarterData.eno1File?.fileName || '';
                const e55FileName = quarterData.e55File?.fileName || '';
                const eno1Status = eno1FileName ? (eno1StatusMap.get(eno1FileName) || '') : '';
                const e55Status = e55FileName ? (e55StatusMap.get(e55FileName) || '') : '';
                
                return (
                  <TableRow key={quarter}>
                    <TableCell>{quarter}</TableCell>
                    <TableCell>{eno1FileName || ''}</TableCell>
                    <TableCell>{eno1Status}</TableCell>
                    <TableCell>{e55FileName || ''}</TableCell>
                    <TableCell>{e55Status}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderRemunerationTableWithTotal = (data: RemunerationData[], totalData: any, sectionTitle: string) => {
    // Check if any row has hasEno1Data: false
    const hasErrorRows = data.some(item => !item.hasEno1Data);
    
    // Define background color based on inbound/outbound
    const cellBackgroundColor = isInbound ? '#e8f5e8' : '#ffeaea'; // Light green for inbound, light red for outbound
    
    // Style for numeric columns (right-aligned)
    const numericCellStyle = { textAlign: 'right' as const };
    // Style for text columns (center-aligned)
    const textCellStyle = { textAlign: 'center' as const };
    
    return (
      <Box sx={{ mb: 4 }}>
        <TableContainer component={Paper} sx={{ boxShadow: 1, mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, ...textCellStyle }}>Remuneration Group</TableCell>
                <TableCell sx={{ fontWeight: 600, ...textCellStyle }}>Format</TableCell>
                <TableCell sx={{ fontWeight: 600, ...textCellStyle }}>Mail Category</TableCell>
                <TableCell sx={{ fontWeight: 600, ...textCellStyle }}>Accounting Type</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>Gross (sampled flow)</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>G-N-AdjF</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>Net%</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>Net (sampled flow)</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>Gross (non-sampled flow)</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>AdjF (non-sampled flow)</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>Net (non-sampled flow)</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>Non-expt-IBRS</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>IB Weight</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>Total kg</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>Rate kg</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>TD kg</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>IPK</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>Items (sampled flow)</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>Items (non-sampled flow)</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>Total items</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>Rate item</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }}>TD item</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item, index) => (
                <TableRow key={index} sx={{ backgroundColor: cellBackgroundColor }}>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...textCellStyle }}>{item.remunerationGroup.displayName}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...textCellStyle }}>{item.formatFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...textCellStyle }}>{item.mailCategoryFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...textCellStyle }}>{item.accountingTypeFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.grossSampledFlowFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.gnAdjFFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.netPercentageFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.netSampledFlowFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.grossNonSampledFlowFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.adjFNonSampledFlowFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.netNonSampledFlowFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.nonExptIbrsFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.ibWeightFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.totalKgFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.rateKgFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.tdKgFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.ipkFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.itemsSampledFlowFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.itemsNonSampledFlowFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.totalItemsFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.rateItemFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }}>{item.tdItemFormatted}</TableCell>
                </TableRow>
              ))}
              {/* Total Row */}
              {totalData && (
                <TableRow sx={{ backgroundColor: '#e3f2fd', fontWeight: 600 }}>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...textCellStyle }}>TOTAL</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...textCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...textCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...textCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}>{totalData.totalKgFormatted}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', backgroundColor: '#ffeb3b', ...numericCellStyle }}>{totalData.rateKgFormatted}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}>{totalData.tdKgFormatted}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}>{totalData.totalItemsFormatted}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', backgroundColor: '#ffeb3b', ...numericCellStyle }}>{totalData.rateItemFormatted}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}>{totalData.tdItemFormatted}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Error message for rows with hasEno1Data: false */}
        {hasErrorRows && (
          <Box sx={{ mt: 1 }}>
            {data
              .filter(item => !item.hasEno1Data)
              .map((item, index) => (
                <Typography 
                  key={index}
                  variant="body2" 
                  sx={{ 
                    color: 'red', 
                    fontStyle: 'italic',
                    mb: 0.5
                  }}
                >
                  No value for [AdjF/Net%/IPK] could be found for {item.remunerationGroupFormatted}, {item.formatFormatted}, {item.mailCategoryFormatted}, {item.accountingTypeFormatted}. A substitution value has been used instead.
                </Typography>
              ))
            }
          </Box>
        )}
      </Box>
    );
  };


  return (
    <Box sx={{ width: '100%', mt: 4 }} data-testid="cn61-crr-html-table-container">
      {/* Report Header */}
      <Box sx={{ mb: 3 }} data-testid="cn61-crr-html-header">
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#063142', mb: 1 }}>
          {title}
        </Typography>
      </Box>



      {/* Combined Files Table */}
      {((data.eno1Files && data.eno1Files.length > 0) || (data.e55Files && data.e55Files.length > 0)) && renderCombinedFileTable()}

      {/* Priority P Data */}
      {data.pgePriority?.p && data.pgePriority.p.length > 0 && (
        renderRemunerationTableWithTotal(data.pgePriority.p, data.pgePriority.totalP, 'Priority P Data')
      )}

      {/* Priority G Data */}
      {data.pgePriority?.g && data.pgePriority.g.length > 0 && (
        renderRemunerationTableWithTotal(data.pgePriority.g, data.pgePriority.totalG, 'Priority G Data')
      )}

      {/* Priority E Data */}
      {data.pgePriority?.e && data.pgePriority.e.length > 0 && (
        renderRemunerationTableWithTotal(data.pgePriority.e, data.pgePriority.totalE, 'Priority E Data')
      )}

      {/* Non-Priority P Data */}
      {data.pgeNonPriority?.p && data.pgeNonPriority.p.length > 0 && (
        renderRemunerationTableWithTotal(data.pgeNonPriority.p, data.pgeNonPriority.totalP, 'Non-Priority P Data')
      )}

      {/* Non-Priority G Data */}
      {data.pgeNonPriority?.g && data.pgeNonPriority.g.length > 0 && (
        renderRemunerationTableWithTotal(data.pgeNonPriority.g, data.pgeNonPriority.totalG, 'Non-Priority G Data')
      )}

      {/* Non-Priority E Data */}
      {data.pgeNonPriority?.e && data.pgeNonPriority.e.length > 0 && (
        renderRemunerationTableWithTotal(data.pgeNonPriority.e, data.pgeNonPriority.totalE, 'Non-Priority E Data')
      )}
    </Box>
  );
}

export default CN61CrrHtmlTable;
